// screens/OrderSearchScreen.js - Screen 8
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { BottomNav } from './HomeScreen';
import { SyncBar, EmptyState, OrderSyncBadge } from '../components/shared';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LanguageContext';
import api from '../utils/api';
import { canSeeOrderComplaints, complaintCounter } from '../utils/complaints';

const FLAG_FILTERS = [
  { key: 'any', label: 'All Flags', color: 'var(--text-secondary)' },
  { key: 'claim', label: 'Has Claim', color: 'var(--accent-danger)' },
  { key: 'marketing', label: 'Marketing', color: 'var(--accent-primary)' },
  { key: 'management', label: 'Management', color: 'var(--accent-warning)' },
];

// Filters survive opening an order and coming back (session-scoped, so a fresh
// app start still begins at the defaults instead of a stale date range).
const FILTER_STORE_KEY = 'photoqa_order_search_filters';
// Empty = no date bound, so search opens on the full order history. The date
// range is opt-in: it only narrows results once someone actually picks a date.
const defaultFromDate = () => '';
const defaultToDate = () => '';
const readSavedFilters = () => {
  try { return JSON.parse(sessionStorage.getItem(FILTER_STORE_KEY)) || {}; }
  catch { return {}; }
};

const PAGE_SIZE = 20;

// Complaint status filter for the service team; offered to complaint roles only.
const COMPLAINT_FILTERS = [
  { value: 'open', key: 'oc.search.open' },
  { value: 'solved', key: 'oc.search.solved' },
  { value: 'any', key: 'oc.search.any' },
];

export default function OrderSearchScreen() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLang();
  const showComplaints = canSeeOrderComplaints(user);
  const [q, setQ] = useState(() => readSavedFilters().q || '');
  const [orders, setOrders] = useState([]);
  const [recentOrders, setRecentOrders] = useState([]);
  const [flaggedOrders, setFlaggedOrders] = useState([]);
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState(() => readSavedFilters().site || '');
  const [selectedFlag, setSelectedFlag] = useState(() => readSavedFilters().flag || '');
  const [complaintStatus, setComplaintStatus] = useState(() => readSavedFilters().complaints || '');
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [searched, setSearched] = useState(false);
  const [page, setPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  // Date range starts empty (all dates) unless the user sets one; a range the
  // user picked survives navigating into an order and back (session-scoped).
  const [fromDate, setFromDate] = useState(() => readSavedFilters().from ?? defaultFromDate());
  const [toDate, setToDate] = useState(() => readSavedFilters().to ?? defaultToDate());

  useEffect(() => {
    sessionStorage.setItem(FILTER_STORE_KEY, JSON.stringify({
      q, site: selectedSite, flag: selectedFlag, from: fromDate, to: toDate, complaints: complaintStatus,
    }));
  }, [q, selectedSite, selectedFlag, fromDate, toDate, complaintStatus]);

  const resetFilters = () => {
    sessionStorage.removeItem(FILTER_STORE_KEY);
    setQ(''); setSelectedSite(''); setSelectedFlag(''); setComplaintStatus('');
    setFromDate(defaultFromDate()); setToDate(defaultToDate());
  };

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      const [recent, flagged, sitesData] = await Promise.all([
        api.get('/api/orders/recent'),
        api.get('/api/orders/flagged'),
        api.get('/api/sites')
      ]);
      setRecentOrders(recent.data);
      setFlaggedOrders(flagged.data);
      setSites(sitesData.data);
    } catch {}
  };

  const buildParams = useCallback((pageNum) => {
    const params = { page: pageNum, limit: PAGE_SIZE };
    if (q) params.q = q;
    if (selectedSite) params.site_id = selectedSite;
    if (selectedFlag) params.flag = selectedFlag;
    if (fromDate) params.from = fromDate;
    if (toDate) params.to = toDate;
    if (showComplaints && complaintStatus) params.complaint_status = complaintStatus;
    return params;
  }, [q, selectedSite, selectedFlag, fromDate, toDate, complaintStatus, showComplaints]);

  const search = useCallback(async () => {
    setLoading(true);
    setSearched(true);
    try {
      const { data } = await api.get('/api/orders', { params: buildParams(1) });
      setOrders(data.orders);
      setTotal(data.total);
      setPage(1);
    } catch {} finally { setLoading(false); }
  }, [buildParams]);

  // Fetch the next page and append, so the full order list is reachable
  // instead of stopping at the first 20 results.
  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const { data } = await api.get('/api/orders', { params: buildParams(page + 1) });
      setOrders((prev) => [...prev, ...data.orders]);
      setTotal(data.total);
      setPage((p) => p + 1);
    } catch {} finally { setLoadingMore(false); }
  };

  // Single source of truth: (re)run the search on mount and whenever ANY filter
  // changes — text, date range, site or flag. `search` is recreated when any of
  // those change, so depending on it covers date-only filtering (empty text).
  useEffect(() => {
    const timer = setTimeout(() => search(), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const handleFlagFilter = (flag) => {
    setSelectedFlag((cur) => (cur === flag ? '' : flag));
  };

  const getFlagBadge = (order) => {
    if (order.has_claim) return <span className="badge badge-danger" style={{ fontSize: '0.625rem' }}>Claim</span>;
    if (order.used_for_marketing) return <span className="badge badge-primary" style={{ fontSize: '0.625rem' }}>Marketing</span>;
    if (order.flagged_by_management) return <span className="badge badge-warning" style={{ fontSize: '0.625rem' }}>Management</span>;
    return null;
  };

  // "1 of 3 solved": only present on orders the server sent the complaint fields for.
  const getComplaintBadge = (order) => {
    const counter = complaintCounter(order);
    if (!counter) return null;
    return (
      <span className={`badge ${counter.allSolved ? 'badge-success' : 'badge-warning'}`} style={{ fontSize: '0.625rem' }}
        title={t('complaints.lines')}>
        {t('complaints.summary').replace('{solved}', counter.solved).replace('{total}', counter.total)}
      </span>
    );
  };

  const OrderCard = ({ order }) => (
    <div className="card card-hover" style={{ padding: '14px 16px' }} onClick={() => navigate(`/order/${order.id}`)}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600, color: 'var(--accent-primary)', fontSize: '0.9375rem' }}>
              {order.order_number}
            </span>
            {getFlagBadge(order)}
            {getComplaintBadge(order)}
            {order.site_name && <span className="site-badge">{order.site_name}</span>}
            <OrderSyncBadge order={order} />
          </div>
          <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {order.product_name}
          </div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 4 }}>
            {order.session_count || 0} session{(order.session_count || 0) !== 1 ? 's' : ''} · {order.photo_count || 0} photos · {new Date(order.created_at).toLocaleDateString()}
            {order.sync_completed_at && <> · synced {new Date(order.sync_completed_at.replace(' ', 'T') + 'Z').toLocaleString([], { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</>}
          </div>
        </div>
        <div style={{ color: 'var(--text-muted)', marginLeft: 8 }}>›</div>
      </div>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', minHeight: '100dvh' }}>
      <SyncBar />
      <div className="page-header">
        <h2>Search Orders</h2>
      </div>

      <div className="page-content">
        {/* Search input */}
        <div className="search-wrap" style={{ marginBottom: 14 }}>
          <div className="search-icon">🔍</div>
          <input
            type="search"
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Order number or product name..."
            className="input-field"
            style={{ fontSize: '1rem' }}
            autoFocus
          />
        </div>

        {/* Date range — empty by default, i.e. search across all dates */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>From</span>
          <input type="date" value={fromDate} max={toDate}
            onChange={e => setFromDate(e.target.value)}
            className="input-field" style={{ minHeight: 38, width: 'auto', fontSize: '0.875rem' }} />
          <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>to</span>
          <input type="date" value={toDate} min={fromDate}
            onChange={e => setToDate(e.target.value)}
            className="input-field" style={{ minHeight: 38, width: 'auto', fontSize: '0.875rem' }} />
          {!fromDate && !toDate && (
            <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>All dates</span>
          )}
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 14, overflow: 'auto', paddingBottom: 2 }}>
          {sites.length > 1 && (
            <select value={selectedSite} onChange={e => setSelectedSite(e.target.value)}
              className="input-field" style={{ minHeight: 38, flex: 'none', maxWidth: 140, fontSize: '0.875rem' }}>
              <option value="">All Sites</option>
              {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          )}
          {showComplaints && (
            <select value={complaintStatus} onChange={e => setComplaintStatus(e.target.value)}
              aria-label={t('oc.search.filter')} className="input-field"
              style={{ minHeight: 38, flex: 'none', maxWidth: 190, fontSize: '0.875rem' }}>
              <option value="">{t('oc.search.filter')}</option>
              {COMPLAINT_FILTERS.map(f => <option key={f.value} value={f.value}>{t(f.key)}</option>)}
            </select>
          )}
          {FLAG_FILTERS.map(f => (
            <button key={f.key}
              onClick={() => handleFlagFilter(f.key)}
              className="btn btn-sm"
              style={{
                background: selectedFlag === f.key ? 'rgba(79,142,247,0.15)' : 'var(--bg-card)',
                border: `1px solid ${selectedFlag === f.key ? 'var(--accent-primary)' : 'var(--border-normal)'}`,
                color: selectedFlag === f.key ? 'var(--accent-primary)' : 'var(--text-secondary)',
                flexShrink: 0
              }}>
              {f.label}
            </button>
          ))}
          <button onClick={resetFilters} className="btn btn-sm" title="Clear search text, dates and filters"
            style={{ background: 'transparent', border: '1px solid var(--border-normal)', color: 'var(--text-muted)', flexShrink: 0 }}>
            ✕ Reset
          </button>
        </div>

        {loading && <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}><div className="spinner" /></div>}

        {/* Search results */}
        {searched && !loading && (
          <div>
            <div className="section-title">{total} result{total !== 1 ? 's' : ''}</div>
            {orders.length === 0 ? (
              <EmptyState icon="🔍" title="No orders found" message="Try a different search term or filter" />
            ) : (
              <>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {orders.map(o => <OrderCard key={o.id} order={o} />)}
                </div>
                {orders.length < total && (
                  <button className="btn btn-secondary btn-full" disabled={loadingMore}
                    onClick={loadMore} style={{ marginTop: 12 }}>
                    {loadingMore ? 'Loading…' : `Load more (${orders.length} of ${total} shown)`}
                  </button>
                )}
              </>
            )}
          </div>
        )}

        {/* Recently flagged */}
        {!searched && flaggedOrders.length > 0 && (
          <div style={{ marginBottom: 24 }}>
            <div className="section-title">Recently Flagged</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {flaggedOrders.slice(0, 5).map(o => <OrderCard key={o.id} order={o} />)}
            </div>
          </div>
        )}

        {/* Recent orders */}
        {!searched && (
          <div>
            <div className="section-title">Recent Orders</div>
            {recentOrders.length === 0 ? (
              <EmptyState icon="📦" title="No orders yet" message="Orders will appear here after the first session" />
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {recentOrders.map(o => <OrderCard key={o.id} order={o} />)}
              </div>
            )}
          </div>
        )}
      </div>

      <BottomNav active="search" />
    </div>
  );
}
